import React, { useState, useEffect } from 'react';
import i18nIsoCountries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';
import CustomTable from 'components/CustomTable/CustomTable';
import { theme, Button, notification, Modal } from 'antd';
import { article8Op } from 'store/ducks/DataManagement/Article8AndICTSystem';
import { IRiskExposure } from 'store/ducks/DataManagement/Article8AndICTSystem/types';
import { generateUniqueId } from 'utils/hooks/generateUniqueId';
import { getRiskExposureColumns } from 'components/Columns/RiskExposure';
import SaveButton from 'components/SaveButton/SaveButton';
import { useTranslation } from 'react-i18next';
import * as Styled from '../../DataEntry/DataEntry.styled';
import { deleteRecord } from 'utils/hooks/deleteRecord';
import useFilters from 'utils/hooks/useFilters';

const RiskExposure: React.FC = () => {
  i18nIsoCountries.registerLocale(enLocale);
  const { query } = useFilters();
  const { useToken } = theme;
  const { t } = useTranslation();
  const [dataSource, setDataSource] = useState<IRiskExposure[]>([]);
  const [count, setCount] = useState(1);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState(10);
  const { token } = useToken();
  const isLoggedIn = localStorage.getItem('authToken');
  const [dependencyTypes, setDependencyTypes] = useState<
    { label: string; value: string }[]
  >([]);
  const [exposureCategories, setExposureCategories] = useState<
    { label: string; value: string }[]
  >([]);
  const { confirm } = Modal;

  const handlePageChange = (page: number, pageSize: number) => {
    setCurrentPage(page);
    setPageSize(pageSize);
  };

  const handleDateChange = (key: number, field: string, dateString: string) => {
    const newData = [...dataSource];
    const index = newData.findIndex((item) => key === item.key);
    if (index > -1) {
      const item = newData[index];
      newData.splice(index, 1, {
        ...item,
        [field]: dateString,
        isEdited: !item.isNew,
      });
      setDataSource(newData);
    }
  };

  const fetchDependencyType = async () => {
    try {
      const response = await article8Op.getDependencyType();
      if (response?.data.dependency_types) {
        const formattedOptions = response.data.dependency_types.map(
          (reasons: { name: any; id: any }) => ({
            label: reasons.name,
            value: reasons.id,
          }),
        );
        setDependencyTypes(formattedOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchExposureCategory = async () => {
    try {
      const response = await article8Op.getExposureCategory();
      if (response?.data.exposure_categories) {
        const formattedOptions = response.data.exposure_categories.map(
          (reasons: { name: any; id: any }) => ({
            label: reasons.name,
            value: reasons.id,
          }),
        );
        setExposureCategories(formattedOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchRiskExposureByEntity = async () => {
    try {
      const response = await article8Op.getRiskExposure();
      const data = response?.data?.risk_exposures;

      const mappedData = Array.isArray(data)
        ? data.map((item, index) => ({
            key: index,
            active: item.active,
            deleted: item.deleted,
            dependency_type_id: item.dependency_type_id,
            start_date: item.start_date,
            end_date: item.end_date,
            description: item.description,
            exposure_category_id: item.exposure_category_id,
            id: item.id,
            name: item.name,
            isNew: false,
            isEdited: false,
          }))
        : data
          ? [
              {
                key: 0,
                active: data.active,
                deleted: data.deleted,
                dependency_type_id: data.dependency_type_id,
                start_date: data.start_date,
                end_date: data.end_date,
                description: data.description,
                exposure_category_id: data.exposure_category_id,
                id: data.id,
                name: data.name,
                isNew: false,
                isEdited: false,
              },
            ]
          : [];

      setDataSource(mappedData);
      setCount(mappedData.length);
    } catch (error) {
      setDataSource([]);
    }
  };

  useEffect(() => {
    fetchDependencyType();
    fetchExposureCategory();
  }, []);

  useEffect(() => {
    if (query.entity) {
      fetchRiskExposureByEntity();
    } else {
      setDataSource([]);
    }
  }, [query.entity]);

  const handleAddRow = (record?: any, index?: number) => {
    const newData: IRiskExposure = {
      key: generateUniqueId(),
      active: true,
      deleted: false,
      dependency_type_id: '',
      start_date: '',
      end_date: '9999-01-01',
      description: '',
      exposure_category_id: '',
      id: generateUniqueId(),
      name: '',
      isNew: true,
    };
    const insertIndex = dataSource.findIndex(
      (item) => item.key === record?.key,
    );

    const updatedDataSource = [
      ...dataSource.slice(0, insertIndex + 1),
      newData,
      ...dataSource.slice(insertIndex + 1),
    ];

    setDataSource(updatedDataSource);
    setCount(count + 1);
  };

  const handleSave = (row: IRiskExposure) => {
    const newData = [...dataSource];
    const index = newData.findIndex((item) => row.key === item.key);
    newData.splice(index, 1, {
      ...newData[index],
      ...row,
      isEdited: !newData[index].isNew,
    });
    setDataSource(newData);
  };

  const handleRemoveRow = (key: number) => {
    Modal.confirm({
      title: 'Are you sure you want to remove this row?',
      content:
        'Once deleted, this record cannot be restored. Would you like to continue?',
      okText: 'Yes',
      cancelText: 'No',
      onOk: () => {
        const updatedDataSource = dataSource.filter((item) => item.key !== key);
        setDataSource(updatedDataSource);
        notification.success({
          message: 'Info',
          description: 'The row has been successfully removed.',
          duration: 20,
        });
      },
      onCancel: () => {
        notification.info({
          message: 'Info',
          description: 'The row removal has been canceled.',
          duration: 20,
        });
      },
    });
  };

  const handleInactivate = (record: IRiskExposure) => {
    if (!record.id) {
      notification.error({
        message: 'Info',
        description: 'Id is missing.',
      });
      return;
    }

    deleteRecord({
      id: record.id,
      tableName: 'risk_exposure',
      onSuccess: () => {
        fetchRiskExposureByEntity();
      },
    });
  };

  const columns = getRiskExposureColumns(
    handleDateChange,
    handleSave,
    handleAddRow,
    handleRemoveRow,
    handleInactivate,
    dependencyTypes,
    exposureCategories,
    t,
  );

  const handleSubmit = async () => {
    const changedData = dataSource.filter(
      (item) => item.isNew || item.isEdited,
    );

    if (changedData.length === 0) {
      setDataSource((prev) =>
        prev.map((item) =>
          item.isNew || item.isEdited
            ? { ...item, isNew: false, isEdited: false }
            : item,
        ),
      );
      notification.info({
        message: 'Info',
        description: 'There are no new or edited data to submit.',
        placement: 'topRight',
        duration: 20,
      });
      return;
    }

    const formattedData = {
      data_list: changedData.map((item) => ({
        id: item.id,
        name: item.name,
        description: item.description,
        start_date: item.start_date,
        end_date: item.end_date,
        dependency_type_id: item.dependency_type_id,
        exposure_category_id: item.exposure_category_id,
      })),
      token: isLoggedIn,
    };

    try {
      const response = await article8Op.insertRiskExposure(formattedData);
      if (response?.status === 200) {
        setDataSource((prev) =>
          prev.map((item) =>
            item.isNew || item.isEdited
              ? { ...item, isNew: false, isEdited: false }
              : item,
          ),
        );
        notification.success({
          message: 'Info',
          description: 'The data was successfully submitted.',
          placement: 'topRight',
          duration: 20,
        });
      }
    } catch (error) {
      notification.error({
        message: 'Info',
        description: 'There was an error sending the data. Please try again.',
        placement: 'topRight',
        duration: 20,
      });
    }
  };

  return (
    dataSource && (
      <>
        <Styled.HighlightedTable>
          <CustomTable
            columns={columns}
            dataSource={dataSource}
            handleSave={handleSave}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              onChange: handlePageChange,
              showSizeChanger: true,
            }}
            locale={{
              emptyText: (
                <Button type="primary" onClick={() => handleAddRow()}>
                  {t('GENERAL.ADD_FIRST_ROW')}
                </Button>
              ),
            }}
          />
        </Styled.HighlightedTable>

        <Styled.ButtonRow>
          {dataSource.length > 0 && (
            <Styled.AreaButton
              token={token}
              type="primary"
              onClick={() => handleAddRow()}
            >
              Add new row
            </Styled.AreaButton>
          )}
          {dataSource.length > 0 && (
            <SaveButton
              token={token}
              hasChanges={dataSource.some((i) => i.isNew || i.isEdited)}
              onClick={handleSubmit}
            />
          )}
        </Styled.ButtonRow>
      </>
    )
  );
};

export default RiskExposure;
